/*
 * Contrato RegistrarSeccionDeNavegacion (RF-018, RF-019, RF-031, D-07, D-10).
 *
 * Cada modulo posterior se integra al shell registrando su seccion, SIN tocar el
 * codigo del shell.
 *
 * REGLAS QUE ESTE REGISTRO HACE CUMPLIR:
 *   * Ajustes es un destino del NUCLEO: no se registra por este contrato, ocupa
 *     siempre el ultimo lugar y ningun modulo puede desplazarlo (RN-044b, RN-044c).
 *   * La clave y la ruta "ajustes" estan reservadas: CLAVE_RESERVADA.
 *   * Maximo CINCO secciones de modulo en la barra inferior; el sexto lugar es de
 *     Ajustes. El excedente pasa al menu (BARRA_INFERIOR_LLENA).
 *   * etiquetaCorta de maximo 10 caracteres (ETIQUETA_CORTA_EXCEDIDA, RNF-022).
 *   * La etiqueta corta del modulo 3/5 es exactamente "Movs" (D-10).
 *   * `visibleEnBarraInferior: false` oculta la seccion de la barra inferior, de
 *     la barra lateral Y del menu de excedentes, sin borrarla del registro: es el
 *     interruptor con que el Tablero queda oculto mientras no tenga contenido
 *     (RN-357, D-220).
 *
 * CAMPO RETIRADO EN EL INCREMENTO 08 (RN-339, D-214): el descriptor ya no lleva
 * `incremento`. Alimentaba un subtitulo con el numero del plan de trabajo, que no
 * es informacion de la persona que usa la aplicacion. El registro propaga el
 * descriptor completo, asi que basta con que los modulos no lo manden: ninguna
 * seccion lo declara y ninguna pantalla lo lee.
 */

export const CLAVE_DE_AJUSTES = 'ajustes';
export const RUTA_DE_AJUSTES = '/ajustes';
export const MAXIMO_DE_SECCIONES_DE_MODULO_EN_BARRA = 5;
export const MAXIMO_DE_CARACTERES_DE_ETIQUETA_CORTA = 10;

/** Destino fijo del nucleo. Lo monta el propio shell, exista o no algun modulo. */
export const SECCION_DE_AJUSTES = Object.freeze({
  clave: CLAVE_DE_AJUSTES,
  ruta: RUTA_DE_AJUSTES,
  etiqueta: 'Ajustes',
  etiquetaCorta: 'Ajustes',
  icono: 'engrane',
  orden: Number.MAX_SAFE_INTEGER,
  visibleEnBarraInferior: true,
  esDelNucleo: true,
});

const secciones = new Map();

export class ErrorDeRegistroDeSeccion extends Error {
  constructor(codigo, mensaje) {
    super(mensaje);
    this.codigo = codigo;
    this.name = 'ErrorDeRegistroDeSeccion';
  }
}

export function registrarSeccion(descriptor) {
  const { clave, ruta, etiqueta, etiquetaCorta, icono, orden, componente } = descriptor ?? {};

  if (!clave || !ruta || !etiqueta || !etiquetaCorta || !icono || orden === undefined) {
    throw new ErrorDeRegistroDeSeccion('DESCRIPTOR_INCOMPLETO',
      'El descriptor de sección requiere clave, ruta, etiqueta, etiquetaCorta, icono y orden.');
  }

  if (clave === CLAVE_DE_AJUSTES || ruta === RUTA_DE_AJUSTES) {
    throw new ErrorDeRegistroDeSeccion('CLAVE_RESERVADA',
      'La clave y la ruta "ajustes" son del núcleo: ningún módulo puede registrarlas.');
  }

  if (secciones.has(clave)) {
    throw new ErrorDeRegistroDeSeccion('SECCION_DUPLICADA',
      `Ya hay una sección registrada con la clave "${clave}".`);
  }

  if (etiquetaCorta.length > MAXIMO_DE_CARACTERES_DE_ETIQUETA_CORTA) {
    throw new ErrorDeRegistroDeSeccion('ETIQUETA_CORTA_EXCEDIDA',
      `"${etiquetaCorta}" supera los ${MAXIMO_DE_CARACTERES_DE_ETIQUETA_CORTA} caracteres permitidos en la barra.`);
  }

  secciones.set(clave, {
    ...descriptor,
    visibleEnBarraInferior: descriptor.visibleEnBarraInferior !== false,
    componente: componente ?? null,
    esDelNucleo: false,
  });

  return obtenerSecciones();
}

/** Solo para pruebas: deja el registro como recién arrancado. */
export function limpiarRegistro() {
  secciones.clear();
}

/**
 * Secciones ordenadas, CON AJUSTES SIEMPRE AL FINAL. El shell lo agrega al armar
 * la lista: no depende de que ningun modulo lo registre ni de su campo orden.
 */
export function obtenerSecciones() {
  const deModulos = [...secciones.values()].sort((a, b) => a.orden - b.orden);
  return [...deModulos, SECCION_DE_AJUSTES];
}

/**
 * Los seis destinos de la barra inferior: hasta cinco de modulos mas Ajustes.
 * Si un modulo pide un sexto lugar, el excedente se va al menu y Ajustes conserva
 * el suyo (RN-044b).
 */
export function obtenerDestinosDeLaBarra() {
  const deModulos = [...secciones.values()]
    .filter((seccion) => seccion.visibleEnBarraInferior)
    .sort((a, b) => a.orden - b.orden)
    .slice(0, MAXIMO_DE_SECCIONES_DE_MODULO_EN_BARRA);

  return [...deModulos, SECCION_DE_AJUSTES];
}

/**
 * ¿ESTE DESTINO ESTÁ ACTIVO? UNA SOLA FUNCIÓN PARA LAS DOS BARRAS (RN-422,
 * RNF-076, corrige CR-05).
 *
 * La regla estaba implementada DOS VECES con DOS CRITERIOS en el mismo archivo:
 * la barra inferior usaba `pathname.startsWith(ruta)` y la lateral, el `isActive`
 * del enlace de navegación. Hoy no divergen por suerte —ninguna ruta de destino
 * es prefijo de otra—, pero es la clase de duplicación que RNF-068 prohíbe, y
 * explica por qué los dos huecos de prueba de la marca visual nunca afirmaron
 * nada: no había UNA sola cosa que afirmar.
 *
 * Lo que esta función garantiza, y es afirmable: estar en una SUBRUTA de un
 * destino marca ese destino y SOLO ese; estar en Ajustes o en una subpantalla de
 * Ajustes marca Ajustes (RN-419); nunca hay dos destinos marcados a la vez. Se
 * compara por SEGMENTO —`/cuentas/tarjetas` sí, `/cuentasx` no— que es lo que
 * `startsWith` pelado no distinguía.
 */
export function esDestinoActivo(rutaDelDestino, rutaActual) {
  if (typeof rutaDelDestino !== 'string' || typeof rutaActual !== 'string') return false;

  const destino = rutaDelDestino.replace(/\/+$/, '');
  const actual = rutaActual.replace(/\/+$/, '');

  if (destino === '') return actual === '';
  return actual === destino || actual.startsWith(`${destino}/`);
}

/** Secciones que no cupieron en la barra y se muestran en el menú secundario. */
export function obtenerExcedenteDelMenu() {
  return [...secciones.values()]
    .filter((seccion) => seccion.visibleEnBarraInferior)
    .sort((a, b) => a.orden - b.orden)
    .slice(MAXIMO_DE_SECCIONES_DE_MODULO_EN_BARRA);
}
